export function analyzeSegments(trackpoints, segmentLength = 200) {
  const segments = [];
  if (!trackpoints || trackpoints.length < 2) return { segments, totalDistance: 0 };

  let segmentStartIdx = 0;

  for (let i = 1; i < trackpoints.length; i++) {
    const segmentDistance = trackpoints[i].cumulativeDistance - trackpoints[segmentStartIdx].cumulativeDistance;

    if (segmentDistance >= segmentLength || i === trackpoints.length - 1) {
      const segmentPoints = trackpoints.slice(segmentStartIdx, i + 1);
      if (segmentPoints.length > 1) {
        const elevations = segmentPoints.map(p => p.ele);
        const startEle = elevations[0];
        const endEle = elevations[elevations.length - 1];

        let totalGain = 0;
        let totalLoss = 0;
        for (let j = 1; j < elevations.length; j++) {
          const diff = elevations[j] - elevations[j - 1];
          if (diff > 0) totalGain += diff;
          else totalLoss += Math.abs(diff);
        }

        const elevationChange = endEle - startEle;
        const gradient = (elevationChange / segmentDistance) * 100;

        segments.push({
          segmentNumber: segments.length + 1,
          distance: segmentDistance,
          elevationGain: totalGain,
          elevationLoss: totalLoss,
          netElevationChange: elevationChange,
          averageGradient: gradient,
          startElevation: startEle,
          endElevation: endEle,
          startTrackpointIndex: segmentStartIdx,
          endTrackpointIndex: i
        });
      }
      segmentStartIdx = i;
    }
  }

  return { segments, totalDistance: trackpoints[trackpoints.length - 1].cumulativeDistance };
}

export function getSectionType(gradient) {
  if (gradient > 20) return { type: 'Very Steep Uphill', class: 'section-very-steep-uphill' };
  if (gradient > 12) return { type: 'Steep Uphill', class: 'section-steep-uphill' };
  if (gradient > 6) return { type: 'Moderate Uphill', class: 'section-moderate-uphill' };
  if (gradient > 2) return { type: 'Gentle Uphill', class: 'section-gentle-uphill' };
  if (gradient >= -2) return { type: 'Flat', class: 'section-flat' };
  if (gradient >= -6) return { type: 'Gentle Downhill', class: 'section-gentle-downhill' };
  if (gradient >= -12) return { type: 'Moderate Downhill', class: 'section-moderate-downhill' };
  if (gradient >= -20) return { type: 'Steep Downhill', class: 'section-steep-downhill' };
  return { type: 'Very Steep Downhill', class: 'section-very-steep-downhill' };
}

export function groupSegmentsIntoSections(segments) {
  if (!segments || segments.length === 0) return [];

  const sections = [];
  let currentSection = {
    sectionNumber: 1,
    type: getSectionType(segments[0].averageGradient).type,
    class: getSectionType(segments[0].averageGradient).class,
    segments: [segments[0]],
    startSegment: 1,
    endSegment: 1
  };

  for (let i = 1; i < segments.length; i++) {
    const currentSegmentType = getSectionType(segments[i].averageGradient);
    if (currentSegmentType.type === currentSection.type) {
      currentSection.segments.push(segments[i]);
      currentSection.endSegment = segments[i].segmentNumber;
    } else {
      sections.push(currentSection);
      currentSection = {
        sectionNumber: sections.length + 1,
        type: currentSegmentType.type,
        class: currentSegmentType.class,
        segments: [segments[i]],
        startSegment: segments[i].segmentNumber,
        endSegment: segments[i].segmentNumber
      };
    }
  }
  sections.push(currentSection);

  let cumulativeDistance = 0;
  return sections.map(section => {
    const totalDistance = section.segments.reduce((sum, seg) => sum + seg.distance, 0);
    const totalElevationGain = section.segments.reduce((sum, seg) => sum + seg.elevationGain, 0);
    const totalElevationLoss = section.segments.reduce((sum, seg) => sum + seg.elevationLoss, 0);
    const netElevationChange = section.segments.reduce((sum, seg) => sum + seg.netElevationChange, 0);
    const averageGradient = totalDistance > 0 ? (netElevationChange / totalDistance) * 100 : 0;
    const startDistance = cumulativeDistance;
    cumulativeDistance += totalDistance;

    return {
      ...section,
      totalDistance,
      totalElevationGain,
      totalElevationLoss,
      netElevationChange,
      averageGradient,
      segmentCount: section.segments.length,
      startDistance,
      endDistance: cumulativeDistance
    };
  });
}

export function createGradientSummary(segments) {
  if (!segments || segments.length === 0) return [];
  const gradients = segments.map(seg => seg.averageGradient);
  const minGradient = Math.min(...gradients);
  const maxGradient = Math.max(...gradients);
  const minRange = Math.floor(minGradient / 2) * 2;
  const maxRange = Math.ceil(maxGradient / 2) * 2;
  const gradientRanges = [];

  for (let i = minRange; i < maxRange; i += 2) {
    gradientRanges.push({ min: i, max: i + 2, label: `${i}% to ${i + 2}%` });
  }

  return gradientRanges.map((range, index) => {
    const isLastRange = index === gradientRanges.length - 1;
    const matchingSegments = segments.filter(seg =>
      seg.averageGradient >= range.min &&
      (isLastRange ? seg.averageGradient <= range.max : seg.averageGradient < range.max)
    );
    const totalDistance = matchingSegments.reduce((sum, seg) => sum + seg.distance, 0);
    const totalElevationChange = matchingSegments.reduce((sum, seg) => sum + seg.netElevationChange, 0);
    const segmentCount = matchingSegments.length;

    return {
      gradientRange: range.label,
      gradientMidpoint: range.min + 1,
      segmentCount,
      totalDistance: totalDistance / 1000,
      totalElevationChange,
      percentage: segments.length > 0 ? ((segmentCount / segments.length) * 100).toFixed(1) : '0'
    };
  });
}

export function calculateSectionStatistics(sections) {
  const stats = {
    uphill: { count: 0, distance: 0, longest: 0 },
    flat: { count: 0, distance: 0, longest: 0 },
    downhill: { count: 0, distance: 0, longest: 0 }
  };

  for (const section of sections) {
    let category;
    if (section.averageGradient > 2) category = 'uphill';
    else if (section.averageGradient >= -2) category = 'flat';
    else category = 'downhill';

    stats[category].count++;
    stats[category].distance += section.totalDistance;
    if (section.totalDistance > stats[category].longest) stats[category].longest = section.totalDistance;
  }

  return stats;
}

